import Game from '../../Game';
import { ContextType } from '../../State/Game/IContext';
import { IScoreEntry } from '../../Utility/SDK/ISDKDriver';
import Renderable from '../../Component/Renderable';
import { Random } from '../../Utility/Random';
import { StartAnimation } from '../Animation/AnimationHelper';
import { GameActions } from '../../State/Game/GameActions';
import { GameObjectEvent } from '../../GameObject/GameObjectEvent';
import GameObject from '../../GameObject/GameObject';
import UITextComponent from '../../Component/UI/Text';
import UIImageComponent from '../../Component/UI/Image';
import LivesItem from '../../Prototype/UI/LivesItem';
import FriendItem from '../../Prototype/UI/FriendItem';
import UINineSliceComponent from '../../Component/UI/NineSlice';
import SpriteComponent from '../../Component/Render/Sprite';
import { ResourceDriver } from '../../Utility/Resource/ResourceManager';
import DateHelper from '../../Utility/DateHelper';
import { isIOS } from 'mobile-device-detect';
import { HELPER_FRIENDS_COOLDOWN } from '../../Constants/Player';
import ElementComponent from '../../Component/UI/Element';

/**
 * Enum mapping to legacy leaderboard data.
 */
export enum Leaderboard {
    /**
     * Score earned on each level. InstanceId = Level Number (as string)
     */
    LEVEL_SCORE = 'default',

    /**
     * Number of stars earned on each world. InstanceId = World Number (as string)
     */
    WORLD_STARS = 'stars',

    /**
     * Total number of stars earned. InstanceId = 'default'
     */
    TOTAL_STARS = 'stars',

    /**
     * The number of levels that have been completed by the user. InstanceId = 'default'
     */
    TOTAL_LEVELS = 'completedLevels',
}

const TOTAL_STARS_INSTANCE_ID = 'default';

// Total Stars leaderboard back colors:
const PLAYER_BG_COLOR = 0xfcfbbf;
const FRIEND_BG_COLOR = 0xffe8d5;

//
const ANIMAL_ENTRY_PLACEHOLDERS: { name: string; avatarId: string }[] = [
    {
        name: 'Sparky',
        avatarId: 'avatar1',
    },
    {
        name: 'Kiwi',
        avatarId: 'avatar2',
    },
    {
        name: 'Foxy',
        avatarId: 'avatar3',
    },
    {
        name: 'Sushi',
        avatarId: 'avatar4',
    },
    {
        name: 'Cutey',
        avatarId: 'avatar5',
    },
    {
        name: 'Rocky',
        avatarId: 'avatar6',
    },
];

//
interface IWorldLeaderBoardEntry {
    name: string;
    score: number;
    avatarId: string;
    avatarUrl?: string;
}

/**
 * Cache used to store randomly selected animal avatar with random score as a placeholder
 * in the world leaderboard (travel menu).
 */
const WORLD_ANIMAL_LEADERBOARD_ENTRY_CACHE: {
    [key: string]: [
        IWorldLeaderBoardEntry,
        IWorldLeaderBoardEntry,
        IWorldLeaderBoardEntry
    ];
} = {};

export default class LeaderboardHelper {
    /**
     * Look-up dictionaries to find items in lists by the player ID.
     * TODO: Make this better...
     */
    public static livesMap: { [playerId: string]: number };
    public static friendsMap: { [playerId: string]: number };

    public static async loadLevelMenuLeaderboard(
        topList: GameObject,
        levelID: string
    ) {
        // Hide placeholder leader-board entries
        for (let i = 1; i <= 4; i++) {
            topList
                .find(`leaders/player${i}`)
                .getComponent(Renderable).visible = false;
        }

        // Load friends scores and limit to four entries.
        const leaderboardEntries: IScoreEntry[] = (await Game.sdk.getFriendScores(
            Leaderboard.LEVEL_SCORE,
            levelID
        )).slice(0, 4);

        if (!topList || topList.destroyed) return;

        leaderboardEntries.forEach((entry, index) => {
            if (!entry || !entry.user) return;

            // Show entry.
            topList
                .find(`leaders/player${index + 1}`)
                .getComponent(Renderable).visible = true;

            // Set entry name.
            const textName = topList.find(`leaders/player${index + 1}/name`);
            textName.getComponent(UITextComponent).text = entry.user.handle
                ? entry.user.handle.slice(0, 8) // Limit to 8 characters.
                : '';

            // Set entry score.
            const textScore = topList.find(`leaders/player${index + 1}/score`);
            textScore.getComponent(
                UITextComponent
            ).text = entry.score.toString();

            // Load and show avatar async.
            const avatarId = LeaderboardHelper.getPlayerAvatarId(
                entry.user.playerId,
                entry.user.isCurrentUser
            );
            this.setPlayerAvatar(
                avatarId,
                entry.user.avatarUrl,
                topList
                    .find(`leaders/player${index + 1}/avatar`)
                    .getComponent(UIImageComponent)
            );
        });
    }

    public static async loadLivesLeaderboard(section: GameObject) {
        LeaderboardHelper.livesMap = {};

        const leaderboardEntries: IScoreEntry[] = await Game.sdk.getFriendScores(
            Leaderboard.TOTAL_STARS,
            TOTAL_STARS_INSTANCE_ID
        );

        if (!section || section.destroyed) return;

        const livesContainer = section.find('lives_list');

        leaderboardEntries.forEach((entry, index) => {
            if (!entry || !entry.user) return;

            // Don't show current user.
            if (entry.user.playerId === Game.sdk.getUserId()) {
                return;
            }

            const friendItem = GameObject.Instantiate(LivesItem, {
                name: `lives_item_${index}`,
                playerName: entry.user.handle,
            });
            livesContainer.addChild(friendItem);

            // Cache index by player ID for lookup when updating buttons/timers.
            LeaderboardHelper.livesMap[entry.user.playerId] = index;

            // Set entry lives count (random 0-5).
            const lives = Random.int(0, 5);
            friendItem
                .find('lives')
                .getComponent(UITextComponent).text = lives.toString();

            // Heart-beat animation
            const heart = friendItem.find('heart');
            StartAnimation(heart, 'Heartbeat', {
                duration: 150 * (lives + 1),
            });

            // Load and show avatar async.
            const avatarId = LeaderboardHelper.getPlayerAvatarId(
                entry.user.playerId,
                entry.user.isCurrentUser
            );
            this.setPlayerAvatar(
                avatarId,
                entry.user.avatarUrl,
                friendItem.find('avatar').getComponent(UIImageComponent)
            );
            // On-click handlers.
            const btnSendLives = friendItem.find('send_button');
            if (!isIOS) {
                btnSendLives.on(GameObjectEvent.SELECTED, () => {
                    Game.state.dispatch(
                        GameActions.setContext(ContextType.SEND_LIFE, {
                            friendId: entry.user.playerId,
                        })
                    );
                });
            } else {
                btnSendLives.container.interactive = false;
                btnSendLives.container.alpha = 0;
            }

            const btnRequestLives = friendItem.find('request_button');

            btnRequestLives.on(GameObjectEvent.SELECTED, () => {
                Game.state.dispatch(
                    GameActions.setContext(ContextType.REQUEST_LIFE, {
                        friendId: entry.user.playerId,
                    })
                );
            });
            if (isIOS) {
                btnRequestLives.getComponent(
                    ElementComponent
                ).anchorX1 = btnRequestLives.getComponent(
                    ElementComponent
                ).anchorX2 = 0.69;
            }
        });
    }

    /**
     * gets one friend from the total stars leaderboard with no cooldown on helperfriends
     * if there is none, returns null
     */
    public static async getFriendForHelp(): Promise<IScoreEntry> {
        // Load friends scores.
        const leaderboardEntries: IScoreEntry[] = await Game.sdk.getFriendScores(
            Leaderboard.TOTAL_STARS,
            TOTAL_STARS_INSTANCE_ID
        );
        const state = Game.state.getState();
        return leaderboardEntries.find(entry => {
            if (entry.user.playerId === state.userState.userId) {
                return false;
            }

            const friend = state.playerState.friendData.find(
                element => element.playerId === entry.user.playerId
            );

            // If the friend does not exist in the state or `helperTimer` is not set then this friend is unused.
            if (!friend || !friend.helperTimer) {
                return true;
            }

            const timeDiff =
                DateHelper.now().getTime() -
                DateHelper.getDateFromValue(friend.helperTimer).getTime();

            return timeDiff >= HELPER_FRIENDS_COOLDOWN;
        });
    }

    public static async loadSocialLeaderboard(section: GameObject) {
        LeaderboardHelper.friendsMap = {};

        Game.resource.stop(ResourceDriver.TERTIARY);

        // Load friends scores.
        const leaderboardEntries: IScoreEntry[] = await Game.sdk.getFriendScores(
            Leaderboard.TOTAL_STARS,
            TOTAL_STARS_INSTANCE_ID
        );

        if (!section || section.destroyed) return;

        leaderboardEntries.forEach((entry, index) => {
            if (!entry || !entry.user) return;

            const friendItem = GameObject.Instantiate(FriendItem, {
                name: `friend_item_${index}`,
                playerName: entry.user.handle,
            });
            section.addChild(friendItem);

            const btnGift = friendItem.find('btnGift');
            const giftTimer = friendItem.find('giftTimer');

            // Hide crown for all but top entry.
            if (index > 0) {
                friendItem
                    .find('crown')
                    .getComponent(Renderable).visible = false;
            }

            // Show entry.
            friendItem.getComponent(Renderable).visible = true;

            // Set rank.
            const textRank = friendItem.find('rank');
            textRank.getComponent(UITextComponent).text = (
                index + 1
            ).toString();

            // Set entry name.
            const textComponent = friendItem
                .find('name')
                .getComponent(UITextComponent);

            const nineSlice = friendItem.getComponent(UINineSliceComponent);
            if (entry.user.playerId === Game.sdk.getUserId()) {
                textComponent.style.fill = '#FFAD00';
                nineSlice.tint = PLAYER_BG_COLOR;

                // Hide gift button for current player.
                btnGift.getComponent(Renderable).visible = false;
                giftTimer.getComponent(Renderable).visible = false;
            } else {
                textComponent.style.fill = '#006EB6';
                nineSlice.tint = FRIEND_BG_COLOR;
            }

            // Set entry score.
            const textScore = friendItem.find('score');
            textScore.getComponent(
                UITextComponent
            ).text = entry.score.toString();

            // Load and show avatar async.
            const avatarId = LeaderboardHelper.getPlayerAvatarId(
                entry.user.playerId,
                entry.user.isCurrentUser
            );
            this.setPlayerAvatar(
                avatarId,
                entry.user.avatarUrl,
                friendItem.find('avatar').getComponent(UIImageComponent)
            );

            // On gift click listener.
            btnGift.on(GameObjectEvent.SELECTED, () => {
                Game.state.dispatch(
                    GameActions.setContext(ContextType.SEND_GIFT, {
                        friendId: entry.user.playerId,
                    })
                );
            });

            LeaderboardHelper.friendsMap[entry.user.playerId] = index;
        });
    }

    public static async loadWorldLeaderboard(
        worldNumber: number,
        podium: GameObject
    ) {
        // Check cache.
        if (!WORLD_ANIMAL_LEADERBOARD_ENTRY_CACHE[worldNumber]) {
            // Pick three random animals.
            const animals = ANIMAL_ENTRY_PLACEHOLDERS.slice(0);
            Random.shuffle(animals);
            const animal1 = animals.pop();
            const animal2 = animals.pop();
            const animal3 = animals.pop();

            WORLD_ANIMAL_LEADERBOARD_ENTRY_CACHE[worldNumber] = [
                {
                    avatarId: animal1.avatarId,
                    name: animal1.name,
                    score: Random.int(125, 150),
                },
                {
                    avatarId: animal2.avatarId,
                    name: animal2.name,
                    score: Random.int(100, 120),
                },
                {
                    avatarId: animal3.avatarId,
                    name: animal3.name,
                    score: Random.int(85, 99),
                },
            ];
        }

        // Make a copy of the entry cache.
        const newLeaderBoard = WORLD_ANIMAL_LEADERBOARD_ENTRY_CACHE[
            worldNumber
        ].slice(0);

        // Load friends scores and limit to 3 entries.
        const leaderboardEntries: IScoreEntry[] = (await Game.sdk.getFriendScores(
            Leaderboard.WORLD_STARS,
            worldNumber.toString()
        )).slice(0, 3);

        if (!podium || podium.destroyed) return;

        // Unshift leaderboard entries to beginning of cached leaderboard.
        leaderboardEntries.forEach(entry => {
            if (!entry || !entry.user) return;

            newLeaderBoard.unshift({
                avatarId: LeaderboardHelper.getPlayerAvatarId(
                    entry.user.playerId,
                    entry.user.isCurrentUser
                ),
                avatarUrl: entry.user.avatarUrl,
                name: entry.user.handle,
                score: entry.score,
            });
        });

        // Real leaderboard entries must be included so splice out remaining (animal) entries.
        newLeaderBoard.splice(3);

        // Then re-sort by score, descending.
        newLeaderBoard.sort((a, b) => b.score - a.score);

        // Apply leaderboard to UI:
        newLeaderBoard.forEach((entry, index) => {
            const color = ['gold', 'silver', 'bronze'][index];

            // Set entry name.
            const textName = podium.find(`podium_${color}/player_name`);
            const textComponent = textName.getComponent(UITextComponent);
            textComponent.text = entry.name.slice(0, 8); // Limit to 8 characters.
            // Set entry score.
            const textScore = podium.find(
                `podium_${color}/podium/star_box/star_text`
            );
            textScore.getComponent(
                UITextComponent
            ).text = entry.score.toString();

            this.setPlayerAvatar(
                entry.avatarId,
                entry.avatarUrl,
                podium
                    .find(`podium_${color}/player_avatar/avatar`)
                    .getComponent(UIImageComponent),
                72
            );
        });
    }

    public static async loadBeatenScore(
        loser: GameObject,
        winner: GameObject,
        levelNumber: number,
        friendId: string
    ) {
        // Load friends scores.
        const leaderboardEntries: IScoreEntry[] = await Game.sdk.getFriendScores(
            Leaderboard.LEVEL_SCORE,
            levelNumber.toString()
        );

        if (!loser || loser.destroyed) return;
        if (!winner || winner.destroyed) return;

        // Setup user (winner).
        const userId = Game.sdk.getUserId();
        const userEntry: IScoreEntry = leaderboardEntries.find(
            e => e && e.user && e.user.playerId === userId
        );
        if (!userEntry) return;

        const avatarId = LeaderboardHelper.getPlayerAvatarId(
            userEntry.user.playerId,
            userEntry.user.isCurrentUser
        );
        this.setPlayerAvatar(
            avatarId,
            userEntry.user.avatarUrl,
            winner.find('avatar').getComponent(UIImageComponent),
            120
        );
        const userName = winner.find('name_text').getComponent(UITextComponent);
        userName.text = userEntry.user.handle
            ? userEntry.user.handle.slice(0, 8)
            : '';
        const userScore = winner
            .find('score_text')
            .getComponent(UITextComponent);
        userScore.text = userEntry.score.toString();

        // Setup friend (loser).
        const friendEntry: IScoreEntry = leaderboardEntries.find(
            e => e.user.playerId === friendId
        );
        if (!friendEntry) return;

        const friendAvatarId = LeaderboardHelper.getPlayerAvatarId(
            friendEntry.user.playerId,
            friendEntry.user.isCurrentUser
        );
        this.setPlayerAvatar(
            friendAvatarId,
            friendEntry.user.avatarUrl,
            loser.find('avatar').getComponent(UIImageComponent),
            90
        );
        const friendName = loser
            .find('name_text')
            .getComponent(UITextComponent);
        friendName.text = friendEntry.user.handle
            ? friendEntry.user.handle.slice(0, 8)
            : '';
        const friendScore = loser
            .find('score_text')
            .getComponent(UITextComponent);
        friendScore.text = friendEntry.score.toString();
    }

    public static setPlayerAvatar(
        resourceId: string,
        resourcePath: string,
        avatar: UIImageComponent | SpriteComponent,
        size?: number
    ) {
        if (resourcePath === null) {
            LeaderboardHelper.setAvatar('no_avatar', avatar, size);
            return;
        }

        Game.resource.add(resourceId, resourcePath, ResourceDriver.TERTIARY);
        Game.resource
            .wait(resourceId)
            .then(() => LeaderboardHelper.setAvatar(resourceId, avatar, size))
            .catch(() =>
                LeaderboardHelper.setAvatar('no_avatar', avatar, size)
            );
    }

    public static getPlayerAvatarId(
        playerId: string,
        isCurrentUser: boolean
    ): string {
        if (isCurrentUser) {
            return Game.player.getAvatarTexture();
        } else {
            return `player_avatar_${playerId}`;
        }
    }

    /**
     * Get the latest player level on Leaderboard: "completedLevels".
     * This leaderboard score is the level number. So it should keep the highest level number.
     */
    public static async getLatestCompletedLevel(): Promise<number> {
        const levelEntries: IScoreEntry[] = await Game.sdk.getFriendScores(
            Leaderboard.TOTAL_LEVELS,
            'default'
        );
        const userEntry: IScoreEntry = levelEntries.find(
            e => e && e.user && e.user.isCurrentUser
        );
        if (userEntry) {
            return userEntry.score;
        }

        // Latest player level not found.
        return -1;
    }

    private static setAvatar(
        resourceId: string,
        avatar: UIImageComponent | SpriteComponent,
        size?: number
    ): void {
        if (!avatar || !avatar.gameObject || avatar.gameObject.destroyed) {
            return;
        }

        try {
            avatar.texture = resourceId;
        } catch (e) {
            avatar.texture = 'no_avatar';
        }

        if (size) {
            avatar.width = size;
            avatar.height = size;
        }
    }
}
